import Catalogs from './catalogs';
import axios from 'axios';
import proj4 from 'proj4';

// Define S-JTSK (EPSG:5514) coordinate system used in Slovak data
proj4.defs(
  'EPSG:5514',
  '+proj=krovak +lat_0=49.5 +lon_0=24.83333333333333 +alpha=30.28813975277778 +k=0.9999 +x_0=0 +y_0=0 +ellps=bessel +towgs84=570.8,85.7,462.8,4.998,1.587,5.261,3.56 +units=m +no_defs'
);

// Polygon lifecycle states. Single source of truth — all polygon-related
// transitions flow through setState() so we cannot end up with stale overlays,
// two polygons on the map, or a drawing in progress while a polygon is active.
const STATE_IDLE = 'IDLE';
const STATE_DRAWING = 'DRAWING';
const STATE_ACTIVE = 'ACTIVE';

class NlcMap {
  map = null;
  state = STATE_IDLE;
  currentPolygon = null; // Set only while state === ACTIVE
  catalogs = null;
  districtPolygons = []; // Static district polygons
  allAreasData = {
    okresy: [], // Districts
    kraje: [], // Regions
    obce: [], // Municipalities
  }; // Store all area data with coordinates
  drawingHistory = []; // History of drawn polygons for undo/redo
  historyIndex = -1; // Current position in history
  areaInfoLabel = null; // Label for displaying area during drawing
  obecLoadedCount = 50; // Number of municipalities initially loaded
  obecBatchSize = 50; // Number to load per scroll
  selectors = {
    container: '.js-nlc-maps-container',
    map: '.js-nlc-maps-map',
    mapWrapper: '.js-nlc-maps-map-wrapper',
    orderBtn: '.js-nlc-maps-order-btn',
    undoBtn: '.js-nlc-maps-undo-btn',
    redoBtn: '.js-nlc-maps-redo-btn',
    togglePreviewsBtn: '.js-nlc-maps-toggle-previews-btn',
    deletePolygonBtn: '.js-nlc-maps-delete-polygon-btn',
    clearPreviewsBtn: '.js-nlc-maps-clear-previews-btn',
    areaInfo: '.js-nlc-maps-area-info',
    areaFilter: '.js-nlc-maps-area-filter',
    areaFilterToggle: '.js-nlc-maps-area-filter-toggle',
    areaFilterContent: '.js-nlc-maps-area-filter-content',
    orderHint: '.js-nlc-maps-order-hint',
  };
  previewsVisible = true;
  hiddenPreviews = {};
  productPreviews = {};

  constructor() {
    this.initializeMap();
    this.initializeDrawingManager();
    this.initializeEventListeners();
    this.initializeAreaInfoLabel();
    this.initializeAreaFilter();
    this.loadAllAreas();
    this.catalogs = new Catalogs();

    // Save initial state to history
    this.saveToHistory();
  }

  // ────────────────────────────────────────────────────────────────────────────
  // Polygon state machine
  //
  // Transitions:
  //   IDLE     → DRAWING  : user clicks empty map area
  //   DRAWING  → ACTIVE   : drawing finished (3+ vertices, dblclick, Enter, marker)
  //   DRAWING  → IDLE     : ESC pressed before completion
  //   *        → ACTIVE   : area picked from filter, polygon restored from history
  //   ACTIVE   → IDLE     : Delete key, toolbar delete button
  //
  // setState(next) is the only path that may swap state — anything outside
  // the controller asks for a transition rather than mutating fields directly.
  // ────────────────────────────────────────────────────────────────────────────

  setState(next, payload = {}) {
    if (next === this.state) {
      // Same state — allow re-entry only for ACTIVE (replacing a polygon)
      if (next !== STATE_ACTIVE) return;
    }
    const prev = this.state;
    this.exitState(prev);
    this.state = next;
    this.enterState(next, payload);
    this.syncUiToState();
  }

  exitState(state) {
    if (state === STATE_DRAWING) {
      if (this.drawingPolyline) {
        this.drawingPolyline.setMap(null);
        this.drawingPolyline = null;
      }
      if (this.drawingRubberBand) {
        this.drawingRubberBand.setMap(null);
        this.drawingRubberBand = null;
      }
      if (this.drawingFirstMarker) {
        this.drawingFirstMarker.setMap(null);
        this.drawingFirstMarker = null;
      }
      this.drawingPoints = [];
      if (this.map) {
        this.map.setOptions({ draggableCursor: null });
      }
    }
    if (state === STATE_ACTIVE) {
      if (this.currentPolygon) {
        this.currentPolygon.setMap(null);
        this.currentPolygon = null;
      }
      this.hideAreaDisplay();
    }
  }

  enterState(state, payload) {
    if (state === STATE_DRAWING) {
      this.drawingPoints = [];
      this.drawingPolyline = new google.maps.Polyline({
        path: [],
        geodesic: false,
        strokeColor: '#ff0000',
        strokeOpacity: 0.9,
        strokeWeight: 2,
        clickable: false,
        map: this.map,
        zIndex: 5,
      });
      // Dashed preview line from the last placed vertex to the mouse cursor.
      // Updated on mousemove so the user sees where the next edge will land.
      this.drawingRubberBand = new google.maps.Polyline({
        path: [],
        geodesic: false,
        strokeColor: '#ff0000',
        strokeOpacity: 0,
        strokeWeight: 2,
        icons: [{
          icon: {
            path: 'M 0,-1 0,1',
            strokeOpacity: 0.7,
            strokeWeight: 2,
            scale: 3,
          },
          offset: '0',
          repeat: '10px',
        }],
        clickable: false,
        map: this.map,
        zIndex: 4,
      });
      this.map.setOptions({ draggableCursor: 'crosshair' });
    }
    if (state === STATE_ACTIVE) {
      const polygon = payload && payload.polygon;
      if (!polygon) {
        console.warn('[NlcMap] enterState(ACTIVE) without polygon — falling back to IDLE');
        this.state = STATE_IDLE;
        return;
      }
      this.currentPolygon = polygon;
      this.addPolygonEditListeners(polygon);
      this.updateAreaDisplay(polygon);
    }
    // IDLE: nothing to set up
  }

  // Sync derived UI (buttons, tile tree, previews) to current state.
  syncUiToState() {
    this.maybeEnableDisableOrderButton();
    if (this.state === STATE_IDLE) {
      // Tile tree lives in the Tabs module — call its public reset method.
      if (window.nlcMaps && window.nlcMaps.tabs && typeof window.nlcMaps.tabs.clearTiles === 'function') {
        window.nlcMaps.tabs.clearTiles();
      }
      this.clearAllProductPreviews();
    }
  }

  // Create a polygon overlay with the standard editable style.
  createPolygon(latLngs) {
    return new google.maps.Polygon({
      paths: latLngs,
      editable: true,
      draggable: true,
      strokeColor: '#ff0000',
      strokeWeight: 2,
      fillColor: '#ff0000',
      fillOpacity: 0.1,
      map: this.map,
      zIndex: 10,
    });
  }

  // Zoom + center to encompass the polygon's bounds.
  fitToPolygon(polygon) {
    const bounds = new google.maps.LatLngBounds();
    polygon.getPath().forEach((p) => bounds.extend(p));
    this.focusOnBounds(bounds);
  }

  initializeMap() {
    const map = document.querySelector(this.selectors.map);
    const zoom = parseFloat(map.getAttribute('data-zoom')) || 17;
    const lat = parseFloat(map.getAttribute('data-lat')) || 48.148597;
    const lng = parseFloat(map.getAttribute('data-lng')) || 17.107748;

    // Slovakia bounds with padding
    const slovakiaBounds = {
      north: 49.7,
      south: 47.5,
      west: 16.5,
      east: 22.9,
    };

    // Map styles are stored on the instance so the order-flow screenshot can
    // forward them to the Static Maps API and produce an image that matches
    // what the user sees on screen (same hidden POIs/roads/labels, same
    // border colour, etc.).
    this.mapStyles = [
        {
          featureType: 'administrative',
          elementType: 'geometry',
          stylers: [
            {
              visibility: 'off',
            },
          ],
        },
        {
          featureType: 'administrative',
          elementType: 'geometry.stroke',
          stylers: [
            {
              visibility: 'off',
            },
            {
              color: '#87f6ff',
            },
            {
              weight: '4',
            },
            {
              gamma: '1',
            },
          ],
        },
        {
          featureType: 'administrative.country',
          elementType: 'all',
          stylers: [
            {
              visibility: 'on',
            },
            {
              color: '#ff7878',
            },
          ],
        },
        {
          featureType: 'administrative.country',
          elementType: 'labels.text',
          stylers: [
            {
              visibility: 'simplified',
            },
          ],
        },
        {
          featureType: 'poi',
          elementType: 'all',
          stylers: [
            {
              visibility: 'off',
            },
          ],
        },
        {
          featureType: 'road',
          elementType: 'all',
          stylers: [
            {
              visibility: 'off',
            },
          ],
        },
        {
          featureType: 'road.arterial',
          elementType: 'all',
          stylers: [
            {
              visibility: 'off',
            },
          ],
        },
        {
          featureType: 'transit',
          elementType: 'all',
          stylers: [
            {
              visibility: 'off',
            },
          ],
        },
      ];

    this.map = new google.maps.Map(map, {
      center: { lat: lat, lng: lng },
      zoom: zoom,
      mapTypeId: 'roadmap',
      disableDefaultUI: true,
      zoomControl: true,
      // Double-click is reserved for finishing a polygon. Zoom is still available
      // via wheel, pinch and the +/- control — preventing the dblclick zoom
      // permanently is simpler and race-free vs toggling per state transition.
      disableDoubleClickZoom: true,
      restriction: {
        latLngBounds: slovakiaBounds,
        strictBounds: false,
      },
      minZoom: 7,
      styles: this.mapStyles,
    });
  }

  // google.maps.drawing.DrawingManager was removed from the Maps JS API in v3.65.
  // We drive polygon drawing manually via the state machine — these fields
  // only carry data while state === DRAWING.
  initializeDrawingManager() {
    this.drawingPoints = [];
    this.drawingPolyline = null;
    this.drawingFirstMarker = null;
  }

  initializeEventListeners() {
    google.maps.event.addListener(
      this.map,
      'click',
      this.handleMapClick.bind(this)
    );

    // Double-click finishes an in-progress polygon
    google.maps.event.addListener(
      this.map,
      'dblclick',
      this.handleMapDoubleClick.bind(this)
    );

    // Mouse move updates the dashed preview line from the last placed vertex
    // to the cursor — rubber-band guide while drawing the polygon.
    google.maps.event.addListener(
      this.map,
      'mousemove',
      this.handleMapMouseMove.bind(this)
    );

    const orderButton = document.querySelector(this.selectors.orderBtn);

    if (orderButton) {
      orderButton.addEventListener('click', (event) => {
        this.handleOrderButtonClick(event, event.currentTarget);
      });
    }

    // Undo/Redo buttons
    const undoBtn = document.querySelector(this.selectors.undoBtn);
    const redoBtn = document.querySelector(this.selectors.redoBtn);

    if (undoBtn) {
      undoBtn.addEventListener('click', () => this.undo());
    }

    if (redoBtn) {
      redoBtn.addEventListener('click', () => this.redo());
    }

    // Toggle previews button
    const togglePreviewsBtn = document.querySelector(
      this.selectors.togglePreviewsBtn
    );
    if (togglePreviewsBtn) {
      togglePreviewsBtn.addEventListener('click', () =>
        this.toggleAllPreviews()
      );
    }

    // Delete polygon button — visible only while a polygon exists
    const deletePolygonBtn = document.querySelector(
      this.selectors.deletePolygonBtn
    );
    if (deletePolygonBtn) {
      deletePolygonBtn.addEventListener('click', () => this.handleDeletePolygonClick());
    }

    // Clear all previews button — visible only when at least one preview exists
    const clearPreviewsBtn = document.querySelector(
      this.selectors.clearPreviewsBtn
    );
    if (clearPreviewsBtn) {
      clearPreviewsBtn.addEventListener('click', () => this.clearAllProductPreviews());
    }

    document.addEventListener('keydown', this.handleKeyDown.bind(this));
  }

  // Handler for the Delete polygon toolbar button — same as Delete key.
  handleDeletePolygonClick() {
    if (this.state !== STATE_ACTIVE) return;
    this.setState(STATE_IDLE);
    // Drop every tile selection too — without this, the polygon is gone but
    // the chips + dashed overlays from the previous polygon's selections
    // remain on screen, which reads as "the trash button did half its job".
    if (window.nlcMaps && window.nlcMaps.tabs && typeof window.nlcMaps.tabs.clearAllSelections === 'function') {
      window.nlcMaps.tabs.clearAllSelections();
    }
    this.saveToHistory();
  }

  // Show/hide the delete polygon button based on whether a polygon is active.
  updateDeletePolygonBtnVisibility() {
    const btn = document.querySelector(this.selectors.deletePolygonBtn);
    if (!btn) return;
    btn.style.display = this.state === STATE_ACTIVE ? '' : 'none';
  }

  // Show/hide the clear-all-previews button based on whether previews exist.
  updateClearPreviewsBtnVisibility() {
    const btn = document.querySelector(this.selectors.clearPreviewsBtn);
    if (!btn) return;
    const hasPreviews =
      this.productPreviews && Object.keys(this.productPreviews).length > 0;
    btn.style.display = hasPreviews ? '' : 'none';
  }

  handleMapClick(event) {
    if (this.state === STATE_DRAWING) {
      this.addDrawingPoint(event.latLng);
      return;
    }

    // Clicking inside the active polygon stays in ACTIVE state (no-op);
    // clicking anywhere else starts a fresh drawing.
    if (this.state === STATE_ACTIVE && this.currentPolygon) {
      const inside = google.maps.geometry.poly.containsLocation(
        event.latLng,
        this.currentPolygon
      );
      if (inside) return;
    }

    this.setState(STATE_DRAWING);
    this.addDrawingPoint(event.latLng);
  }

  handleMapMouseMove(event) {
    if (this.state !== STATE_DRAWING || !this.drawingRubberBand) return;
    if (this.drawingPoints.length === 0) return;
    const lastPoint = this.drawingPoints[this.drawingPoints.length - 1];
    this.drawingRubberBand.setPath([lastPoint, event.latLng]);
  }

  handleMapDoubleClick() {
    if (this.state === STATE_DRAWING && this.drawingPoints.length >= 3) {
      this.finishDrawing();
    }
  }

  // Append a vertex to the in-progress polygon and update the preview polyline.
  // First point also gets a clickable marker so the user can close the ring on it.
  addDrawingPoint(latLng) {
    if (this.state !== STATE_DRAWING) return;

    this.drawingPoints.push(latLng);
    if (this.drawingPolyline) {
      this.drawingPolyline.setPath(this.drawingPoints);
    }

    if (this.drawingPoints.length === 1) {
      this.drawingFirstMarker = new google.maps.Marker({
        position: latLng,
        map: this.map,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 7,
          fillColor: '#ff0000',
          fillOpacity: 1,
          strokeColor: '#ffffff',
          strokeWeight: 2,
        },
        cursor: 'pointer',
        zIndex: 12,
      });
      google.maps.event.addListener(this.drawingFirstMarker, 'click', () => {
        if (this.drawingPoints.length >= 3) {
          this.finishDrawing();
        }
      });
    }
  }

  // Convert the in-progress polyline into a proper editable Polygon via the state machine.
  finishDrawing() {
    if (this.state !== STATE_DRAWING || this.drawingPoints.length < 3) return;

    const points = this.drawingPoints.slice();
    const polygon = this.createPolygon(points);

    // ACTIVE replaces any prior polygon and tears down DRAWING artifacts.
    this.setState(STATE_ACTIVE, { polygon });
    this.saveToHistory();
    this.fitToPolygon(polygon);
    this.onPolygonReady(polygon);
  }

  async handleOrderButtonClick(event, orderButton) {
    event.preventDefault();

    if (!this.currentPolygon) return;

    const productId = orderButton.getAttribute('data-product-id');
    if (!productId) {
      console.error('Product ID is missing');
      return;
    }

    // Stop any background work so it can't compete with the order submission
    // (tile streaming, pending polygon fetch).
    if (this._tilesByPolygonAbort) {
      this._tilesByPolygonAbort.abort();
      this._tilesByPolygonAbort = null;
    }
    if (window.nlcMaps && window.nlcMaps.tabs && typeof window.nlcMaps.tabs.cancelInFlight === 'function') {
      window.nlcMaps.tabs.cancelInFlight();
    }

    // No need to add the .nlc-maps loading class — the fullscreen overlay
    // already covers the whole viewport, including the map area.
    const fullscreenLoader = this.showFullscreenLoader('Spracovávam objednávku…');

    // Yield once so the loader paints before we start any work.
    await new Promise((resolve) => requestAnimationFrame(resolve));

    // Compute a tight view (center + zoom) on the polygon for the static map.
    // Use the live map div's aspect ratio so the framed view matches what the
    // user sees — but clamp to Static Maps' 640px free-tier ceiling per side.
    const polygonBounds = new google.maps.LatLngBounds();
    this.currentPolygon.getPath().forEach((p) => polygonBounds.extend(p));
    const mapDiv = this.map.getDiv();
    const rawW = mapDiv.clientWidth || 640;
    const rawH = mapDiv.clientHeight || 480;
    const aspect = rawW / rawH;
    let staticWidth;
    let staticHeight;
    if (aspect >= 1) {
      staticWidth = 640;
      staticHeight = Math.max(240, Math.round(640 / aspect));
    } else {
      staticHeight = 640;
      staticWidth = Math.max(240, Math.round(640 * aspect));
    }

    // Pad the bounds with ~7 % on every side so the polygon never touches the
    // image edge. `floor(log2(...))` already rounds to "fits with margin", but
    // edge-tight zoom levels still looked clipped on screen (a vertex landing
    // 2 px from the top of the picture reads as cropped to a human eye).
    // Expanding the bounds first guarantees a visible breathing strip around
    // the polygon — even if the static image's aspect doesn't perfectly match
    // the live map div's aspect.
    const ne = polygonBounds.getNorthEast();
    const sw = polygonBounds.getSouthWest();
    const latBuf = (ne.lat() - sw.lat()) * 0.07;
    const lngBuf = (ne.lng() - sw.lng()) * 0.07;
    polygonBounds.extend(new google.maps.LatLng(ne.lat() + latBuf, ne.lng() + lngBuf));
    polygonBounds.extend(new google.maps.LatLng(sw.lat() - latBuf, sw.lng() - lngBuf));

    // Zoom must be computed against the static image dimensions, NOT the live
    // map div — otherwise a wide polygon shown on a 1200×800 map would crop in
    // a 640×480 static image.
    const padPx = 20;
    const staticZoom = this.calculateZoomForSize(
      polygonBounds,
      staticWidth,
      staticHeight,
      padPx
    );
    const staticCenter = polygonBounds.getCenter();

    // Encode the polygon as a Google polyline string so it fits comfortably
    // in the Static Maps URL even for ~1000-vertex shapes. Static Maps treats
    // `path` as a polyline — `fillcolor` closes the *fill* but NOT the
    // outline. Without explicitly repeating the first vertex at the end, the
    // closing edge (last → first) is missing from the stroke. Build a fresh
    // array, append the first point, encode.
    const livePath = this.currentPolygon.getPath();
    const closedPath = [];
    livePath.forEach((p) => closedPath.push(p));
    if (closedPath.length > 0) {
      closedPath.push(closedPath[0]);
    }
    const encodedPath = google.maps.geometry.encoding.encodePath(closedPath);

    // Forward the live map's style rules so the static image matches the
    // on-screen design (hidden POIs/roads, custom border colour, …).
    const staticStyles = this._stylesToStaticMapStrings(this.mapStyles);

    try {
      // Ask the server to fetch a Static Maps screenshot. Google renders the
      // image on its side; we just store the resulting JPG. This replaces the
      // old html2canvas pipeline, which froze the browser ("Stránka nereaguje")
      // because it walked the entire map DOM (tiles + 200 SVG overlays)
      // synchronously on the main thread.
      const screenshotForm = new FormData();
      screenshotForm.append('action', 'nlc_map_save_screenshot');
      screenshotForm.append('nonce', nlc_map_settings.save_screenshot_nonce);
      screenshotForm.append('encoded_path', encodedPath);
      screenshotForm.append('center', `${staticCenter.lat()},${staticCenter.lng()}`);
      screenshotForm.append('zoom', String(staticZoom));
      screenshotForm.append('width', String(staticWidth));
      screenshotForm.append('height', String(staticHeight));
      staticStyles.forEach((s) => screenshotForm.append('styles[]', s));

      const screenshotResponse = await axios.post(
        nlc_map_settings.ajax_url,
        screenshotForm
      );
      const screenshotUrl =
        screenshotResponse &&
        screenshotResponse.data &&
        screenshotResponse.data.success &&
        screenshotResponse.data.data &&
        screenshotResponse.data.data.url;
      if (!screenshotUrl) {
        // Surface the server-side failure reason so the console error is
        // actionable (e.g. "Static Maps returned HTTP 403" → enable Maps
        // Static API in the GCP console). The old generic message hid the
        // root cause.
        const errMessage =
          (screenshotResponse &&
            screenshotResponse.data &&
            screenshotResponse.data.data &&
            screenshotResponse.data.data.message) ||
          'unknown reason';
        throw new Error('Screenshot generation failed: ' + errMessage);
      }

      const mapCenter = this.map.getCenter();
      const area = this.calculatePolygonArea(this.currentPolygon);

      // Total *tile* area (NOT polygon) — what we bill for. Polygons often
      // extend beyond the selected tiles; charging by polygon would overprice
      // the user. Tabs.getSelectedTilesArea sums each ticked tile's km².
      const tileStats = (window.nlcMaps && window.nlcMaps.tabs && typeof window.nlcMaps.tabs.getSelectedTilesArea === 'function')
        ? window.nlcMaps.tabs.getSelectedTilesArea()
        : { count: 0, areaKm2: 0 };

      const cartItemData = {
        [nlc_map_settings.choosen_products_meta_key]:
          this.catalogs.getCheckedCatalogs(),
        [nlc_map_settings.map_polygon_meta_key]: this.getPolygonCoordinates(
          this.currentPolygon
        ),
        [nlc_map_settings.map_screenshot_meta_key]: screenshotUrl,
        [nlc_map_settings.map_coords_meta_key]: `${mapCenter.lat()},${mapCenter.lng()},${this.map.getZoom()}`,
        [nlc_map_settings.map_area_meta_key]: area.km2,
        [nlc_map_settings.tile_area_meta_key]: tileStats.areaKm2,
        [nlc_map_settings.tile_count_meta_key]: tileStats.count,
      };

      const formData = new FormData();
      formData.append('product_id', productId);
      formData.append('cart_item_data', JSON.stringify(cartItemData));

      await axios.post(
        wc_add_to_cart_params.wc_ajax_url
          .toString()
          .replace('%%endpoint%%', 'add_to_cart'),
        formData
      );

      window.location.href = nlc_map_settings.cart_url;
    } catch (error) {
      console.error('Order submit failed:', error);
      if (fullscreenLoader) fullscreenLoader.remove();
    }
  }

  // Fullscreen loader overlay shown while submitting an order. Covers the
  // whole page (z-index above the walkthrough) so the user cannot interact
  // with stale UI during the submission.
  showFullscreenLoader(message) {
    let overlay = document.getElementById('nlc-maps-fullscreen-loader');
    if (overlay) {
      const text = overlay.querySelector('.nlc-maps-fullscreen-loader__text');
      if (text) text.textContent = message || '';
      return overlay;
    }
    overlay = document.createElement('div');
    overlay.id = 'nlc-maps-fullscreen-loader';
    overlay.style.cssText =
      'position:fixed;inset:0;z-index:2147483647;background:rgba(255,255,255,0.85);' +
      'display:flex;flex-direction:column;align-items:center;justify-content:center;' +
      'gap:18px;font-family:Source Sans Pro,Arial,sans-serif;color:#1f2937;';
    overlay.innerHTML = `
      <div style="width:56px;height:56px;border:5px solid #cfe1ee;border-top-color:#0065b3;border-radius:50%;animation:nlc-fullscreen-spin 0.9s linear infinite;"></div>
      <div class="nlc-maps-fullscreen-loader__text" style="font-size:16px;font-weight:600;">${this._escapeText(message || '')}</div>
      <style>@keyframes nlc-fullscreen-spin{to{transform:rotate(360deg);}}</style>
    `;
    document.body.appendChild(overlay);
    return overlay;
  }

  _escapeText(s) {
    const d = document.createElement('div');
    d.textContent = s;
    return d.innerHTML;
  }

  handleKeyDown(event) {
    if (event.key === 'Escape' && this.state === STATE_DRAWING) {
      this.setState(STATE_IDLE);
      return;
    }
    if (event.key === 'Enter' && this.state === STATE_DRAWING && this.drawingPoints.length >= 3) {
      this.finishDrawing();
      return;
    }
    if (event.key === 'Delete' && this.state === STATE_ACTIVE) {
      this.setState(STATE_IDLE);
      this.saveToHistory();
      return;
    }

    // Keyboard shortcuts for undo/redo
    if ((event.ctrlKey || event.metaKey) && event.key === 'z') {
      event.preventDefault();
      if (event.shiftKey) {
        this.redo();
      } else {
        this.undo();
      }
    }
  }

  getPolygonCoordinates(polygon) {
    if (polygon) {
      return polygon
        .getPath()
        .getArray()
        .map((latlng) => ({
          lat: latlng.lat(),
          lng: latlng.lng(),
        }));
    }
    return [];
  }

  maybeEnableDisableOrderButton() {
    this.updateDeletePolygonBtnVisibility();
    const orderButton = document.querySelector(this.selectors.orderBtn);

    if (!orderButton) {
      return;
    }

    const productId = orderButton.getAttribute('data-product-id');

    if (!productId) {
      console.log('Product ID does not exist or is empty');
      this.disableOrderButton();
      this.updateOrderHint('polygon', 'products'); // Both missing in this error case
      return;
    }

    const polygon = this.currentPolygon;
    const catalogs = this.catalogs ? this.catalogs.getCheckedCatalogs() : [];
    const hasPolygon = !!polygon;
    const hasProducts = catalogs && catalogs.length > 0;

    if (!hasPolygon && !hasProducts) {
      this.disableOrderButton();
      this.updateOrderHint('polygon', 'products');
      return;
    }

    if (!hasPolygon) {
      this.disableOrderButton();
      this.updateOrderHint('polygon');
      return;
    }

    if (!hasProducts) {
      this.disableOrderButton();
      this.updateOrderHint('products');
      return;
    }

    // Hard cap on map sheets per order — anything beyond is rejected with
    // an explanation, so the user knows exactly why the button is grey.
    const maxTiles = (nlc_map_settings && nlc_map_settings.max_tiles != null)
      ? Number(nlc_map_settings.max_tiles)
      : 4;
    if (catalogs.length > maxTiles) {
      this.disableOrderButton();
      this.updateOrderHint('tooMany');
      return;
    }

    this.enableOrderButton();
    this.updateOrderHint(); // Clear hint when enabled
  }

  updateOrderHint(...missing) {
    const hintElement = document.querySelector(this.selectors.orderHint);
    if (!hintElement) return;

    if (missing.length === 0) {
      hintElement.textContent = '';
      hintElement.style.display = 'none';
      return;
    }

    const hints = [];
    if (missing.includes('polygon')) {
      hints.push(
        nlc_map_settings.hint_draw_polygon || 'Draw a polygon on the map'
      );
    }
    if (missing.includes('products')) {
      hints.push(
        nlc_map_settings.hint_select_product || 'Select at least one product'
      );
    }
    if (missing.includes('tooMany')) {
      const max = (nlc_map_settings && nlc_map_settings.max_tiles != null)
        ? Number(nlc_map_settings.max_tiles)
        : 4;
      hints.push(
        nlc_map_settings.hint_too_many_tiles ||
          ('Prekročili ste rozsah povolenej plochy. Max ' + max + ' mapové listy.')
      );
    }

    hintElement.textContent = hints.join(' • ');
    hintElement.style.display = 'block';
  }

  enableOrderButton() {
    document.querySelector(this.selectors.orderBtn).removeAttribute('disabled');
  }

  disableOrderButton() {
    document
      .querySelector(this.selectors.orderBtn)
      .setAttribute('disabled', 'true');
  }

  isPolygonSelected(polygon) {
    return polygon === this.currentPolygon;
  }

  // Convert a google.maps.Polygon into a GeoJSON Polygon (WGS84).
  // Uses the outer ring only, closes it, and simplifies down to the API's
  // 1000-point limit (with headroom — we aim for ~800 max).
  polygonToGeoJSON(polygon) {
    if (!polygon) return null;
    const paths = polygon.getPaths();
    if (!paths || paths.getLength() === 0) return null;

    let ring = [];
    const outer = paths.getAt(0);
    for (let i = 0; i < outer.getLength(); i++) {
      const pt = outer.getAt(i);
      ring.push([pt.lng(), pt.lat()]);
    }
    if (ring.length < 3) return null;

    // Server-side limit is 1000 vertices — keep some headroom for the closing point.
    ring = this.simplifyRing(ring, 800);

    const first = ring[0];
    const last = ring[ring.length - 1];
    if (first[0] !== last[0] || first[1] !== last[1]) {
      ring.push([first[0], first[1]]);
    }
    return { type: 'Polygon', coordinates: [ring] };
  }

  // Reduce a ring's vertex count to at most maxPoints using iterative Douglas-Peucker.
  // Starts with a small tolerance and doubles it until the result fits the limit.
  simplifyRing(ring, maxPoints) {
    if (!Array.isArray(ring) || ring.length <= maxPoints) return ring;

    // Start at ~1 m in WGS84 degrees, then escalate
    let tolerance = 0.00001;
    let result = ring;
    let iterations = 0;
    while (result.length > maxPoints && iterations < 25) {
      result = this.douglasPeuckerRing(ring, tolerance);
      tolerance *= 2;
      iterations++;
    }
    return result;
  }

  // Iterative Douglas-Peucker over [[lng, lat], ...] points.
  douglasPeuckerRing(points, epsilon) {
    const n = points.length;
    if (n < 3) return points;

    const keep = new Array(n).fill(false);
    keep[0] = true;
    keep[n - 1] = true;

    const stack = [[0, n - 1]];
    while (stack.length > 0) {
      const [start, end] = stack.pop();
      if (end - start < 2) continue;

      let maxDist = 0;
      let maxIndex = start;
      const a = points[start];
      const b = points[end];

      for (let i = start + 1; i < end; i++) {
        const d = this.perpendicularDistDegrees(points[i], a, b);
        if (d > maxDist) {
          maxDist = d;
          maxIndex = i;
        }
      }

      if (maxDist > epsilon) {
        keep[maxIndex] = true;
        stack.push([start, maxIndex]);
        stack.push([maxIndex, end]);
      }
    }

    const out = [];
    for (let i = 0; i < n; i++) {
      if (keep[i]) out.push(points[i]);
    }
    return out;
  }

  // Perpendicular distance from p to segment ab in degree-space.
  // Acceptable for short ranges; we're not doing geodesic math.
  perpendicularDistDegrees(p, a, b) {
    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    if (dx === 0 && dy === 0) {
      const ex = p[0] - a[0];
      const ey = p[1] - a[1];
      return Math.sqrt(ex * ex + ey * ey);
    }
    const num = Math.abs(dy * p[0] - dx * p[1] + b[0] * a[1] - b[1] * a[0]);
    return num / Math.sqrt(dx * dx + dy * dy);
  }

  // Fire whenever the active polygon changes (drawn, generated, edited).
  // Cancels any in-flight request and triggers tile filter.
  onPolygonReady(polygon) {
    if (!polygon) return;
    const geo = this.polygonToGeoJSON(polygon);
    if (!geo) return;

    if (this._tilesByPolygonAbort) {
      this._tilesByPolygonAbort.abort();
    }
    this._tilesByPolygonAbort = new AbortController();

    if (window.nlcMaps && window.nlcMaps.tabs && typeof window.nlcMaps.tabs.loadByPolygon === 'function') {
      window.nlcMaps.tabs.loadByPolygon(geo, { signal: this._tilesByPolygonAbort.signal });
    }
  }

  // Frame a polygon's bounds inside the map viewport.
  //
  // Strategy: set zoom first, then pan. `restriction` on the map (slovakiaBounds)
  // can block fitBounds from centring exactly on the target when the polygon is
  // near the country edge — the map's anti-overshoot pulls the view back, leaving
  // the polygon shifted in the viewport. Zoom is unaffected by restriction, so
  // doing it first guarantees the right scale; panTo then settles as close to
  // the target centre as the restriction allows.
  focusOnBounds(bounds, paddingPx) {
    if (!bounds || bounds.isEmpty() || !this.map) return;

    const defaultPad = window.innerWidth <= 767 ? 40 : 60;
    const pad = paddingPx != null ? paddingPx : defaultPad;

    const targetZoom = this.calculateBoundsZoom(bounds, pad);
    if (this.map.getZoom() !== targetZoom) {
      this.map.setZoom(targetZoom);
    }
    this.map.panTo(bounds.getCenter());
  }

  // Maximum zoom level at which the given bounds still fit inside the map
  // viewport with `padding` pixels of margin on every side.
  calculateBoundsZoom(bounds, padding) {
    const GLOBE_WIDTH = 256;
    const ZOOM_MAX = 18;
    const div = this.map.getDiv();

    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();

    const latFraction =
      (this._latRad(ne.lat()) - this._latRad(sw.lat())) / Math.PI;
    let lngDiff = ne.lng() - sw.lng();
    if (lngDiff < 0) lngDiff += 360;
    const lngFraction = lngDiff / 360;

    const w = Math.max(div.offsetWidth - padding * 2, 1);
    const h = Math.max(div.offsetHeight - padding * 2, 1);

    const lngZoom = Math.floor(Math.log2(w / GLOBE_WIDTH / lngFraction));
    const latZoom = Math.floor(Math.log2(h / GLOBE_WIDTH / latFraction));

    return Math.max(Math.min(lngZoom, latZoom, ZOOM_MAX), 7);
  }

  _latRad(lat) {
    const sin = Math.sin((lat * Math.PI) / 180);
    const rad = Math.log((1 + sin) / (1 - sin)) / 2;
    return Math.max(Math.min(rad, Math.PI), -Math.PI) / 2;
  }

  // Same maths as calculateBoundsZoom but takes explicit canvas dimensions so
  // we can sizing-check against the Static Maps image (which is clamped to
  // 640×640 — a different aspect ratio than the live map div). Without this,
  // a wide polygon shown on a 1200×800 map div would crop in a 640×480 static
  // image because the zoom was picked for the bigger canvas.
  calculateZoomForSize(bounds, width, height, padding) {
    const GLOBE_WIDTH = 256;
    const ZOOM_MAX = 18;

    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();

    const latFraction =
      (this._latRad(ne.lat()) - this._latRad(sw.lat())) / Math.PI;
    let lngDiff = ne.lng() - sw.lng();
    if (lngDiff < 0) lngDiff += 360;
    const lngFraction = lngDiff / 360;

    const w = Math.max(width - padding * 2, 1);
    const h = Math.max(height - padding * 2, 1);

    const lngZoom = Math.floor(Math.log2(w / GLOBE_WIDTH / lngFraction));
    const latZoom = Math.floor(Math.log2(h / GLOBE_WIDTH / latFraction));

    return Math.max(Math.min(lngZoom, latZoom, ZOOM_MAX), 7);
  }

  // Convert the live map's `styles` array (Google Maps JS API format) into the
  // `style=` parameter strings the Static Maps API expects, so the screenshot
  // matches the on-screen design (hidden POIs, custom country border colour,
  // simplified labels, …).
  //
  // Static Maps format:  style=feature:F|element:E|key:value|key2:value2
  // Colours must use `0xRRGGBB` instead of `#RRGGBB`.
  _stylesToStaticMapStrings(styles) {
    if (!Array.isArray(styles)) return [];
    return styles
      .map((rule) => {
        const parts = [];
        if (rule.featureType) parts.push('feature:' + rule.featureType);
        if (rule.elementType) parts.push('element:' + rule.elementType);
        if (Array.isArray(rule.stylers)) {
          rule.stylers.forEach((styler) => {
            Object.keys(styler).forEach((key) => {
              let val = styler[key];
              if (typeof val === 'string' && val.startsWith('#')) {
                val = '0x' + val.slice(1);
              }
              parts.push(key + ':' + val);
            });
          });
        }
        return parts.join('|');
      })
      .filter(Boolean);
  }

  // Parse WKT POLYGON string to Google Maps coordinates.
  // Skips individual malformed coordinate pairs instead of failing the whole polygon —
  // some source data has Z/M dimensions or non-finite values mixed in.
  parseWKT(wkt) {
    try {
      if (!wkt || typeof wkt !== 'string') {
        return [];
      }

      const match = wkt.match(/POLYGON\s*\(\(([^)]+)\)\)/i);
      if (!match || !match[1]) {
        return [];
      }

      const pairs = match[1].split(',');
      const result = [];
      let skipped = 0;

      for (let i = 0; i < pairs.length; i++) {
        const parts = pairs[i].trim().split(/\s+/);
        const x = Number(parts[0]);
        const y = Number(parts[1]);
        if (!Number.isFinite(x) || !Number.isFinite(y)) {
          skipped++;
          continue;
        }
        try {
          const [lng, lat] = proj4('EPSG:5514', 'EPSG:4326', [x, y]);
          if (Number.isFinite(lat) && Number.isFinite(lng)) {
            result.push({ lat, lng });
          } else {
            skipped++;
          }
        } catch (e) {
          skipped++;
        }
      }

      if (skipped > 0) {
        console.warn(`[nlc-maps] parseWKT skipped ${skipped} invalid coordinate pair(s) of ${pairs.length}`);
      }
      return result;
    } catch (error) {
      console.error('Error parsing WKT:', error);
      return [];
    }
  }

  // Convert pre-parsed S-JTSK coord pairs [[x, y], ...] to Google Maps {lat, lng}.
  // Same defensive skipping as parseWKT.
  sjtskCoordsToLatLng(coords) {
    if (!Array.isArray(coords) || coords.length === 0) {
      return [];
    }
    const result = [];
    let skipped = 0;
    for (let i = 0; i < coords.length; i++) {
      const pair = coords[i];
      if (!Array.isArray(pair) || pair.length < 2) {
        skipped++;
        continue;
      }
      const x = Number(pair[0]);
      const y = Number(pair[1]);
      if (!Number.isFinite(x) || !Number.isFinite(y)) {
        skipped++;
        continue;
      }
      try {
        const [lng, lat] = proj4('EPSG:5514', 'EPSG:4326', [x, y]);
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          result.push({ lat, lng });
        } else {
          skipped++;
        }
      } catch (e) {
        skipped++;
      }
    }
    if (skipped > 0) {
      console.warn(`[nlc-maps] sjtskCoordsToLatLng skipped ${skipped} invalid coordinate pair(s) of ${coords.length}`);
    }
    return result;
  }

  // Parse CSV data
  parseCSV(text) {
    const lines = text.trim().split('\n');
    const headers = lines[0].split(',').map((h) => h.trim());
    const data = [];

    for (let i = 1; i < lines.length; i++) {
      // Handle quoted values that may contain commas
      const row = {};
      let currentValue = '';
      let insideQuotes = false;
      let headerIndex = 0;

      for (let j = 0; j < lines[i].length; j++) {
        const char = lines[i][j];

        if (char === '"') {
          insideQuotes = !insideQuotes;
        } else if (char === ',' && !insideQuotes) {
          row[headers[headerIndex]] = currentValue.trim();
          currentValue = '';
          headerIndex++;
        } else {
          currentValue += char;
        }
      }
      // Add last value
      row[headers[headerIndex]] = currentValue.trim();

      data.push(row);
    }

    return data;
  }

  // Load area names from database via AJAX (no WKT data - loaded on demand)
  async loadAllAreas() {
    try {
      const response = await fetch(nlc_map_settings.ajax_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          action: 'nlc_map_get_areas',
        }),
      });

      const result = await response.json();

      if (!result.success || !result.data) {
        throw new Error('Failed to load areas data');
      }

      const areasData = result.data;

      // Kraje + okresy come with pre-fetched S-JTSK coords (bulk preload).
      // Convert once and cache in memory — first click is then instant.
      const buildArea = (row, type) => ({
        id: row.id,
        name: row.name,
        type,
        coordinates:
          Array.isArray(row.coords) && row.coords.length
            ? this.sjtskCoordsToLatLng(row.coords)
            : null,
      });

      areasData.kraje.forEach((row) => {
        this.allAreasData.kraje.push(buildArea(row, 'kraj'));
      });

      areasData.okresy.forEach((row) => {
        this.allAreasData.okresy.push(buildArea(row, 'okres'));
      });

      // Obce stay lazy — too many rows to bulk preload
      areasData.obce.forEach((row) => {
        this.allAreasData.obce.push({
          id: row.id,
          name: row.name,
          type: 'obec',
          coordinates: null,
        });
      });

      // Sort all data by name
      this.allAreasData.okresy.sort((a, b) =>
        a.name.localeCompare(b.name, 'sk')
      );
      this.allAreasData.kraje.sort((a, b) =>
        a.name.localeCompare(b.name, 'sk')
      );
      this.allAreasData.obce.sort((a, b) => a.name.localeCompare(b.name, 'sk'));

      // Populate filter
      this.populateAreaFilter();

    } catch (error) {
      console.error('Error loading areas:', error);
    }
  }

  // Load WKT data for specific area(s) on demand
  async loadAreaWkt(areaIds) {
    if (!Array.isArray(areaIds)) {
      areaIds = [areaIds];
    }

    try {
      const formData = new FormData();
      formData.append('action', 'nlc_map_get_area_wkt');
      areaIds.forEach((id) => formData.append('ids[]', id));

      const response = await fetch(nlc_map_settings.ajax_url, {
        method: 'POST',
        body: formData,
      });

      const result = await response.json();

      if (!result.success || !result.data) {
        throw new Error('Failed to load area WKT data');
      }

      return result.data;
    } catch (error) {
      console.error('Error loading area WKT:', error);
      return null;
    }
  }

  // Initialize area info label
  initializeAreaInfoLabel() {
    // Class must follow the BEM rule in _map.scss (.nlc-maps__area-info).
    // Previously the inline cssText hard-coded styling, which beat any CSS
    // override and prevented responsive font sizing.
    const areaInfoDiv = document.createElement('div');
    areaInfoDiv.className = 'nlc-maps__area-info js-nlc-maps-area-info';
    document.querySelector(this.selectors.mapWrapper).appendChild(areaInfoDiv);
    this.areaInfoLabel = areaInfoDiv;
  }

  // Calculate area of polygon in m², km², and ha
  calculatePolygonArea(polygon) {
    if (!polygon) return { m2: 0, km2: 0, ha: 0 };

    const areaM2 = google.maps.geometry.spherical.computeArea(
      polygon.getPath()
    );
    const areaKm2 = areaM2 / 1000000;
    const areaHa = areaM2 / 10000;

    return {
      m2: areaM2,
      km2: areaKm2,
      ha: areaHa,
    };
  }

  // Update area display
  updateAreaDisplay(polygon) {
    if (!polygon || !this.areaInfoLabel) return;

    const area = this.calculatePolygonArea(polygon);

    this.areaInfoLabel.innerHTML = `
      <span class="nlc-maps__area-info-label"><strong>Plocha:</strong> </span>
      <span class="nlc-maps__area-info-value-ha">${area.ha.toFixed(2)} ha</span>
      <span class="nlc-maps__area-info-sep"> | </span>
      <span class="nlc-maps__area-info-value-km">${area.km2.toFixed(2)} km²</span>
    `;
    this.areaInfoLabel.style.display = 'block';
  }

  // Hide area display
  hideAreaDisplay() {
    if (this.areaInfoLabel) {
      this.areaInfoLabel.style.display = 'none';
    }
  }

  // Save current state to history
  saveToHistory() {
    // Remove any history after current index (for redo)
    this.drawingHistory = this.drawingHistory.slice(0, this.historyIndex + 1);

    // Save current polygon state and previews state
    const previewsData = {};
    if (this.productPreviews) {
      Object.keys(this.productPreviews).forEach((id) => {
        previewsData[id] = {
          bounds: this.productPreviews[id].bounds,
          title: this.productPreviews[id].title,
          mode: this.productPreviews[id].mode || 'preview',
        };
      });
    }

    // Get current map view
    const center = this.map.getCenter();
    const zoom = this.map.getZoom();

    const state = {
      polygon: this.currentPolygon
        ? { coordinates: this.getPolygonCoordinates(this.currentPolygon) }
        : null,
      previews: previewsData,
      hiddenPreviews: { ...this.hiddenPreviews },
      previewsVisible: this.previewsVisible,
      mapView: {
        center: { lat: center.lat(), lng: center.lng() },
        zoom: zoom,
      },
    };

    this.drawingHistory.push(state);
    this.historyIndex++;

    this.updateHistoryButtons();
  }

  // Undo last action
  undo() {
    if (this.historyIndex > 0) {
      this.historyIndex--;
      this.restoreFromHistory();
      this.updateHistoryButtons();
    }
  }

  // Redo last undone action
  redo() {
    if (this.historyIndex < this.drawingHistory.length - 1) {
      this.historyIndex++;
      this.restoreFromHistory();
      this.updateHistoryButtons();
    }
  }

  // Restore polygon from history — drives transition via the state machine.
  restoreFromHistory() {
    // Tear down previews (state machine owns polygon only — previews are separate)
    if (this.productPreviews) {
      Object.keys(this.productPreviews).forEach((id) => {
        if (this.productPreviews[id].polygon) {
          this.productPreviews[id].polygon.setMap(null);
        }
        if (this.productPreviews[id].label) {
          this.productPreviews[id].label.setMap(null);
        }
      });
    }
    this.productPreviews = {};

    const state = this.drawingHistory[this.historyIndex];

    if (
      state &&
      state.polygon &&
      state.polygon.coordinates &&
      state.polygon.coordinates.length > 0
    ) {
      const path = state.polygon.coordinates.map(
        (coord) => new google.maps.LatLng(coord.lat, coord.lng)
      );
      const polygon = this.createPolygon(path);
      this.setState(STATE_ACTIVE, { polygon });
      this.onPolygonReady(polygon);
    } else {
      this.setState(STATE_IDLE);
    }

    // Restore previews — reuse the same style helper so undo lands in the
    // same visual state as the live map (dashed polylines, selected = brand
    // blue thicker, preview = muted grey thinner).
    if (state && state.previews) {
      Object.keys(state.previews).forEach((id) => {
        const data = state.previews[id];
        const restoredMode = data.mode === 'selected' ? 'selected' : 'preview';
        const style = this._previewStyleFor(restoredMode);
        const path = this._closedPathFromBounds(data.bounds);

        const previewPolygon = new google.maps.Polyline({
          path,
          clickable: true,
          strokeOpacity: 0,
          icons: this._dashedLineIcons(style.dashRepeat),
          strokeColor: style.strokeColor,
          strokeWeight: style.strokeWeight,
          map: this.map,
          zIndex: style.zIndexPolyline,
        });
        google.maps.event.addListener(previewPolygon, 'click', () => {
          this.toggleProductPreview(id, data.bounds, data.title, restoredMode);
        });

        const center = this.boundsCenter(data.bounds);

        const label = new google.maps.Marker({
          position: { lat: center.lat, lng: center.lng },
          map: this.map,
          clickable: true,
          title: (nlc_map_settings.click_to_hide_preview || 'Click to hide preview'),
          label: {
            text: (data.title || id) + '  ×',
            color: style.labelColor,
            fontWeight: 'bold',
            fontSize: '11px',
            className: 'nlc-maps-product-label',
          },
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 0,
          },
          cursor: 'pointer',
          zIndex: style.zIndexLabel,
        });
        google.maps.event.addListener(label, 'click', () => {
          this.toggleProductPreview(id, data.bounds, data.title, restoredMode);
        });

        this.productPreviews[id] = {
          polygon: previewPolygon,
          label: label,
          bounds: data.bounds,
          title: data.title,
          mode: restoredMode,
        };
      });
    }

    // Restore hidden previews and visibility state
    this.hiddenPreviews =
      state && state.hiddenPreviews ? { ...state.hiddenPreviews } : {};
    this.previewsVisible = state ? state.previewsVisible : true;

    // Update UI
    this.updateTogglePreviewsButton();
    const toggleBtn = document.querySelector(this.selectors.togglePreviewsBtn);
    if (toggleBtn) {
      if (
        this.previewsVisible &&
        Object.keys(this.productPreviews).length > 0
      ) {
        toggleBtn.classList.add('active');
      } else {
        toggleBtn.classList.remove('active');
      }
    }

    // Update preview buttons in sidebar
    document.querySelectorAll('.js-nlc-maps-preview-btn').forEach((btn) => {
      const id = btn.getAttribute('data-id');
      if (this.productPreviews[id]) {
        btn.classList.add('active');
      } else {
        btn.classList.remove('active');
      }
    });

    // Restore map view (zoom and center)
    if (state && state.mapView) {
      this.map.setCenter(
        new google.maps.LatLng(
          state.mapView.center.lat,
          state.mapView.center.lng
        )
      );
      this.map.setZoom(state.mapView.zoom);
    }

    this.maybeEnableDisableOrderButton();
  }

  // Add edit listeners to polygon for area updates and history
  addPolygonEditListeners(polygon) {
    const updateArea = () => this.updateAreaDisplay(polygon);

    // Debounce history saves AND tile refetch - vertex drags fire many events
    let historyTimeout = null;
    let refetchTimeout = null;
    const saveAfterEdit = () => {
      updateArea();
      clearTimeout(historyTimeout);
      historyTimeout = setTimeout(() => this.saveToHistory(), 300);

      // Refresh tile list once the user stops editing (heavier debounce)
      clearTimeout(refetchTimeout);
      refetchTimeout = setTimeout(() => this.onPolygonReady(polygon), 600);
    };

    google.maps.event.addListener(polygon.getPath(), 'set_at', saveAfterEdit);
    google.maps.event.addListener(polygon.getPath(), 'insert_at', saveAfterEdit);
    google.maps.event.addListener(polygon.getPath(), 'remove_at', saveAfterEdit);
    google.maps.event.addListener(polygon, 'dragend', saveAfterEdit);
  }

  // Update undo/redo button states
  updateHistoryButtons() {
    const undoBtn = document.querySelector(this.selectors.undoBtn);
    const redoBtn = document.querySelector(this.selectors.redoBtn);

    if (undoBtn) {
      undoBtn.disabled = this.historyIndex <= 0;
    }

    if (redoBtn) {
      redoBtn.disabled = this.historyIndex >= this.drawingHistory.length - 1;
    }
  }

  // Initialize district search UI
  // Initialize area filter UI
  initializeAreaFilter() {
    const filterDiv = document.createElement('div');
    filterDiv.className = 'nlc-maps-area-filter js-nlc-maps-area-filter';
    filterDiv.innerHTML = `
      <button type="button" class="nlc-maps-area-filter__toggle js-nlc-maps-area-filter-toggle" title="Filter oblastí" aria-label="Filter oblastí">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M3 6h18M3 12h18M3 18h18"></path>
        </svg>
        <span>Filter oblastí</span>
      </button>
      <div class="nlc-maps-area-filter__content js-nlc-maps-area-filter-content" style="display:none;">
        <div class="nlc-maps-area-filter__header">
          <h3>Výber oblastí</h3>
          <p class="nlc-maps-area-filter__desc">Kliknite na oblasť — polygón sa nakreslí automaticky</p>
          <div class="nlc-maps-area-filter__search">
            <input
              type="text"
              class="nlc-maps-area-filter__search-input js-nlc-maps-area-search"
              placeholder="Hľadať oblasť..."
              aria-label="Hľadať oblasť"
            />
          </div>
        </div>
        <div class="nlc-maps-area-filter__scroll">
          <div class="nlc-maps-area-filter__sections"></div>
        </div>
      </div>
    `;

    const mapWrapper = document.querySelector(this.selectors.mapWrapper);
    if (mapWrapper) {
      mapWrapper.appendChild(filterDiv);

      // Toggle filter visibility
      const toggleBtn = filterDiv.querySelector(
        this.selectors.areaFilterToggle
      );
      const content = filterDiv.querySelector(this.selectors.areaFilterContent);

      toggleBtn.addEventListener('click', () => {
        const isVisible = content.style.display !== 'none';
        content.style.display = isVisible ? 'none' : 'block';
        toggleBtn.classList.toggle('active', !isVisible);
      });

      // Search input
      const searchInput = filterDiv.querySelector('.js-nlc-maps-area-search');
      if (searchInput) {
        searchInput.addEventListener('input', (e) => {
          this.filterAreasBySearch(e.target.value);
        });

        // Keyboard navigation
        searchInput.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            this.selectFirstVisibleArea();
          }
        });
      }
    }
  }

  // Populate area filter with all areas
  populateAreaFilter() {
    const sections = document.querySelector('.nlc-maps-area-filter__sections');
    if (!sections) return;

    const categories = [
      { key: 'kraje', label: 'Kraje', data: this.allAreasData.kraje },
      { key: 'okresy', label: 'Okresy', data: this.allAreasData.okresy },
      {
        key: 'obce',
        label: `Obce (${this.obecLoadedCount} z ${this.allAreasData.obce.length})`,
        data: this.allAreasData.obce.slice(0, this.obecLoadedCount),
        hasMore: this.obecLoadedCount < this.allAreasData.obce.length,
      },
    ];

    categories.forEach(({ key, label, data, hasMore }) => {
      if (data.length === 0) return;

      const section = document.createElement('div');
      section.className = 'nlc-maps-area-filter__section';
      section.setAttribute('data-section-key', key);
      section.innerHTML = `
        <h4 class="nlc-maps-area-filter__section-title">${label}</h4>
        <div class="nlc-maps-area-filter__items" data-items-key="${key}"></div>
        ${
          hasMore
            ? '<div class="nlc-maps-area-filter__load-more" data-load-more="' +
              key +
              '">Načítať ďalších ' +
              this.obecBatchSize +
              '...</div>'
            : ''
        }
      `;

      const itemsContainer = section.querySelector(
        '.nlc-maps-area-filter__items'
      );

      data.forEach((area) => {
        this.createAreaFilterItem(area, itemsContainer);
      });

      sections.appendChild(section);

      // Add load more button handler
      if (hasMore) {
        const loadMoreBtn = section.querySelector(
          '.nlc-maps-area-filter__load-more'
        );
        if (loadMoreBtn) {
          loadMoreBtn.addEventListener('click', () => this.loadMoreObce());
        }
      }
    });
  }

  // Create area filter item — single click immediately draws that area's polygon.
  createAreaFilterItem(area, container) {
    const item = document.createElement('button');
    item.type = 'button';
    item.className = 'nlc-maps-area-filter__item';
    item.setAttribute('data-type', area.type);
    item.innerHTML = `<span>${area.name}</span>`;
    item.addEventListener('click', () => {
      this.handleAreaSelection(area);
    });
    container.appendChild(item);
  }

  // Load more municipalities
  loadMoreObce() {
    const newCount = Math.min(
      this.obecLoadedCount + this.obecBatchSize,
      this.allAreasData.obce.length
    );

    const itemsContainer = document.querySelector('[data-items-key="obce"]');
    const loadMoreBtn = document.querySelector('[data-load-more="obce"]');
    const sectionTitle = document.querySelector(
      '[data-section-key="obce"] .nlc-maps-area-filter__section-title'
    );

    if (!itemsContainer) return;

    // Add new items
    const newItems = this.allAreasData.obce.slice(
      this.obecLoadedCount,
      newCount
    );
    newItems.forEach((area) => {
      this.createAreaFilterItem(area, itemsContainer);
    });

    this.obecLoadedCount = newCount;

    // Update title
    if (sectionTitle) {
      sectionTitle.textContent = `Obce (${this.obecLoadedCount} z ${this.allAreasData.obce.length})`;
    }

    // Update or remove load more button
    if (loadMoreBtn) {
      if (this.obecLoadedCount >= this.allAreasData.obce.length) {
        loadMoreBtn.remove();
      } else {
        const remaining = this.allAreasData.obce.length - this.obecLoadedCount;
        loadMoreBtn.textContent = `Načítať ďalších ${Math.min(
          this.obecBatchSize,
          remaining
        )}...`;
      }
    }
  }

  // Picking an area from the filter immediately replaces the current polygon
  // with the area's boundary via setState(ACTIVE) — single-polygon invariant
  // is enforced by the state machine.
  async handleAreaSelection(area) {
    if (!area) return;

    // Feedback: the WKT fetch for obce + map polygon creation can take a moment.
    // Surface a loading indicator immediately so the user knows their click registered.
    if (window.nlcMaps && window.nlcMaps.tabs && typeof window.nlcMaps.tabs.showLoading === 'function') {
      window.nlcMaps.tabs.showLoading(
        nlc_map_settings.loading_area || 'Pripravujem polygón…'
      );
    }

    // Load coords if not preloaded (obce path — kraje/okresy come pre-fetched)
    if (!area.coordinates) {
      const wktData = await this.loadAreaWkt(area.id);
      const entry = wktData && wktData[area.id];
      if (entry) {
        if (Array.isArray(entry.coords) && entry.coords.length) {
          area.coordinates = this.sjtskCoordsToLatLng(entry.coords);
        } else if (entry.wkt) {
          area.coordinates = this.parseWKT(entry.wkt);
        }
      }
    }

    if (!Array.isArray(area.coordinates) || area.coordinates.length === 0) {
      console.warn('Area has no coordinates, skipping:', area.id, area.name);
      return;
    }

    const path = area.coordinates.map(
      (c) => new google.maps.LatLng(c.lat, c.lng)
    );
    const polygon = this.createPolygon(path);

    // Replaces any prior polygon AND clears any drawing in progress.
    this.setState(STATE_ACTIVE, { polygon });
    this.saveToHistory();
    this.fitToPolygon(polygon);
    this.onPolygonReady(polygon);
  }

  // Filter areas by search query
  filterAreasBySearch(query) {
    const normalizedQuery = query.toLowerCase().trim();
    const sections = document.querySelectorAll(
      '.nlc-maps-area-filter__section'
    );

    if (!normalizedQuery) {
      // Reset to normal view
      sections.forEach((section) => {
        const sectionKey = section.getAttribute('data-section-key');
        const itemsContainer = section.querySelector(
          '.nlc-maps-area-filter__items'
        );
        const loadMoreBtn = section.querySelector(
          '.nlc-maps-area-filter__load-more'
        );

        if (sectionKey === 'obce') {
          // Restore original loaded obce
          itemsContainer.innerHTML = '';
          const loadedObce = this.allAreasData.obce.slice(
            0,
            this.obecLoadedCount
          );
          loadedObce.forEach((area) => {
            this.createAreaFilterItem(area, itemsContainer);
          });

          // Show load more button if needed
          if (loadMoreBtn) {
            loadMoreBtn.style.display =
              this.obecLoadedCount < this.allAreasData.obce.length
                ? 'block'
                : 'none';
          }
        } else {
          // For kraje and okresy, just show all items
          const sectionItems = section.querySelectorAll(
            '.nlc-maps-area-filter__item'
          );
          sectionItems.forEach((item) => {
            item.style.display = 'flex';
          });
        }

        section.style.display = 'block';
      });
      return;
    }

    // Search in all data (including non-rendered obce)
    sections.forEach((section) => {
      const sectionKey = section.getAttribute('data-section-key');
      const itemsContainer = section.querySelector(
        '.nlc-maps-area-filter__items'
      );
      const loadMoreBtn = section.querySelector(
        '.nlc-maps-area-filter__load-more'
      );

      // Hide load more button during search
      if (loadMoreBtn) {
        loadMoreBtn.style.display = 'none';
      }

      if (sectionKey === 'obce') {
        // For obce, search in ALL data and render matching results
        const matchingObce = this.allAreasData.obce.filter((obec) =>
          obec.name.toLowerCase().includes(normalizedQuery)
        );

        // Clear current items
        itemsContainer.innerHTML = '';

        // Add matching items
        matchingObce.forEach((area) => {
          this.createAreaFilterItem(area, itemsContainer);
        });

        section.style.display = matchingObce.length > 0 ? 'block' : 'none';
      } else {
        // For kraje and okresy, filter existing items
        const sectionItems = section.querySelectorAll(
          '.nlc-maps-area-filter__item'
        );
        let visibleCount = 0;

        sectionItems.forEach((item) => {
          const name = item.querySelector('span').textContent.toLowerCase();
          const matches = name.includes(normalizedQuery);

          item.style.display = matches ? 'flex' : 'none';
          if (matches) visibleCount++;
        });

        section.style.display = visibleCount > 0 ? 'block' : 'none';
      }
    });
  }

  // Select first visible area when Enter is pressed
  // Enter in the area filter search → activate the first visible item.
  selectFirstVisibleArea() {
    const visibleItems = Array.from(
      document.querySelectorAll('.nlc-maps-area-filter__item')
    ).filter((item) => item.style.display !== 'none');

    if (visibleItems.length > 0) {
      visibleItems[0].click();
    }
  }

  // Visual styles for the two tile overlay modes. Both render as dashed
  // *polylines* (no fill) so the user's drawn polygon — solid red with light
  // fill — visually dominates the map. With ~200 selected tiles, the map
  // never gets covered in opaque blue boxes that fight the polygon for
  // attention.
  //
  // `selected` = tile is in the order → brand-blue dashes, slightly thicker
  // `preview`  = just an eye-icon look → muted-grey dashes, thinner
  _previewStyleFor(mode) {
    if (mode === 'selected') {
      return {
        strokeColor: '#0065b3',
        strokeWeight: 2.5,
        labelColor: '#0065b3',
        dashRepeat: '14px',
        zIndexPolyline: 2,
        zIndexLabel: 6,
      };
    }
    return {
      strokeColor: '#5a6b85',
      strokeWeight: 1.5,
      labelColor: '#5a6b85',
      dashRepeat: '10px',
      zIndexPolyline: 1,
      zIndexLabel: 5,
    };
  }

  // Build the Polyline `icons` config that renders a dashed line by repeating
  // a small vertical segment along an otherwise-invisible polyline. Standard
  // Google Maps technique — Polyline has no native dash support.
  _dashedLineIcons(repeat) {
    return [{
      icon: {
        path: 'M 0,-1 0,1',
        strokeOpacity: 1,
        scale: 3,
      },
      offset: '0',
      repeat: repeat || '12px',
    }];
  }

  // Toggle product preview on map (show/hide bounds rectangle). Default mode
  // is 'preview' (eye-button exploration). When called from a checkbox change
  // the caller passes mode='selected' to get the stronger filled style. If a
  // 'preview' overlay already exists and we get mode='selected', the style is
  // upgraded in place (no destroy/recreate). On a plain toggle from the same
  // mode the overlay is removed.
  toggleProductPreview(productId, bounds, title, mode) {
    if (!this.productPreviews) {
      this.productPreviews = {};
    }
    const requestedMode = mode === 'selected' ? 'selected' : 'preview';

    const existing = this.productPreviews[productId];
    if (existing) {
      // Same caller hit toggle again → remove. But if a 'preview' overlay is
      // being upgraded to 'selected' (or vice versa), re-style in place.
      if (existing.mode === requestedMode) {
        existing.polygon.setMap(null);
        existing.label.setMap(null);
        delete this.productPreviews[productId];
        this.updateTogglePreviewsButton();
        this.saveToHistory();
        return;
      }
      const styleUpgrade = this._previewStyleFor(requestedMode);
      existing.polygon.setOptions({
        strokeColor: styleUpgrade.strokeColor,
        strokeWeight: styleUpgrade.strokeWeight,
        icons: this._dashedLineIcons(styleUpgrade.dashRepeat),
        zIndex: styleUpgrade.zIndexPolyline,
      });
      existing.label.setOptions({
        zIndex: styleUpgrade.zIndexLabel,
      });
      const labelOpts = existing.label.getLabel();
      if (labelOpts && typeof labelOpts === 'object') {
        existing.label.setLabel(Object.assign({}, labelOpts, { color: styleUpgrade.labelColor }));
      }
      existing.mode = requestedMode;
      return;
    }

    // Dashed Polyline border (Polygon has no native dash support — we use a
    // closed polyline with a repeating vertical-segment icon to fake it).
    // Polyline path doesn't auto-close, so we append the first vertex.
    const path = this._closedPathFromBounds(bounds);
    const style = this._previewStyleFor(requestedMode);

    const previewPolygon = new google.maps.Polyline({
      path,
      clickable: true,
      strokeOpacity: 0,
      icons: this._dashedLineIcons(style.dashRepeat),
      strokeColor: style.strokeColor,
      strokeWeight: style.strokeWeight,
      map: this.map,
      zIndex: style.zIndexPolyline,
    });
    // Click anywhere on the preview rectangle removes it
    google.maps.event.addListener(previewPolygon, 'click', () => {
      this.toggleProductPreview(productId, bounds, title, requestedMode);
    });

    // Calculate center for label
    const center = this.boundsCenter(bounds);
    const centerLat = center.lat;
    const centerLng = center.lng;

    // Create label marker — the " ×" suffix hints the preview can be dismissed
    const label = new google.maps.Marker({
      position: { lat: centerLat, lng: centerLng },
      map: this.map,
      clickable: true,
      title: (nlc_map_settings.click_to_hide_preview || 'Click to hide preview'),
      label: {
        text: (title || productId) + '  ×',
        color: style.labelColor,
        fontWeight: 'bold',
        fontSize: '11px',
        className: 'nlc-maps-product-label',
      },
      icon: {
        path: google.maps.SymbolPath.CIRCLE,
        scale: 0,
      },
      cursor: 'pointer',
      zIndex: style.zIndexLabel,
    });
    google.maps.event.addListener(label, 'click', () => {
      this.toggleProductPreview(productId, bounds, title, requestedMode);
    });

    // Update toggle button to active state when first preview is added
    this.updateTogglePreviewsButton();
    const toggleBtn = document.querySelector(this.selectors.togglePreviewsBtn);
    if (toggleBtn && this.previewsVisible) {
      toggleBtn.classList.add('active');
    }

    this.productPreviews[productId] = {
      polygon: previewPolygon,
      label: label,
      bounds: bounds,
      title: title,
      mode: requestedMode,
    };

    // Update toggle button visibility
    this.updateTogglePreviewsButton();

    // Zoom to the bounds with max zoom limit
    const mapBounds = new google.maps.LatLngBounds();
    path.forEach((point) => mapBounds.extend(point));
    this.focusOnBounds(mapBounds, 50);

    // Save state AFTER action
    this.saveToHistory();
  }

  // Create editable polygon from product bounds
  createPolygonFromBounds(bounds) {
    const path = this.boundsToPath(bounds);
    const polygon = this.createPolygon(path);

    this.setState(STATE_ACTIVE, { polygon });
    this.saveToHistory();
    this.fitToPolygon(polygon);
    this.onPolygonReady(polygon);
  }

  // Replace the current polygon with the convex hull of every supplied
  // corner. Used by the toolbar "Vytvoriť polygón z vybraných" button — the
  // user can select N tiles in the sidebar and have the polygon re-shape to
  // hug all of them ("zemiak" / potato outline).
  //
  // `allCorners` is a flat array of {x: lng, y: lat} points pooled from each
  // selected tile's wgs84Corners. We compute the 2D convex hull (Andrew's
  // monotone chain) and feed it through createPolygon so the resulting
  // polygon is fully editable + history-tracked like any drawn one.
  createPolygonFromCorners(allCorners) {
    if (!Array.isArray(allCorners) || allCorners.length < 3) return null;
    const hull = this._convexHull2D(allCorners);
    if (hull.length < 3) return null;
    const path = hull.map((c) => new google.maps.LatLng(c.y, c.x));
    const polygon = this.createPolygon(path);

    this.setState(STATE_ACTIVE, { polygon });
    this.saveToHistory();
    this.fitToPolygon(polygon);
    this.onPolygonReady(polygon);
    return polygon;
  }

  // Andrew's monotone chain — O(n log n) convex hull. Sorts points by x then
  // y, builds lower then upper hull, concatenates. Cross product < 0 keeps
  // CCW orientation; <= 0 would drop collinear points. Returns points in
  // counter-clockwise order.
  _convexHull2D(points) {
    if (!Array.isArray(points) || points.length < 3) return points || [];
    const sorted = points.slice().sort((a, b) => a.x - b.x || a.y - b.y);
    const cross = (o, a, b) =>
      (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);

    const lower = [];
    for (const p of sorted) {
      while (
        lower.length >= 2 &&
        cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0
      ) {
        lower.pop();
      }
      lower.push(p);
    }
    const upper = [];
    for (let i = sorted.length - 1; i >= 0; i--) {
      const p = sorted[i];
      while (
        upper.length >= 2 &&
        cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0
      ) {
        upper.pop();
      }
      upper.push(p);
    }
    upper.pop();
    lower.pop();
    return lower.concat(upper);
  }

  // Convert bounds object or corners array to polygon path.
  // Accepts:
  //   - Array of corner points: [{x, y}, ...] (precise polygon shape from wgs84Corners)
  //   - Bbox object: {minX, minY, maxX, maxY}
  //   - Legacy: {x, y, width, height}
  boundsToPath(bounds) {
    // Array of corners → use directly
    if (Array.isArray(bounds) && bounds.length >= 3) {
      return bounds.map((c) => new google.maps.LatLng(c.y, c.x));
    }

    let minX, minY, maxX, maxY;
    if (bounds.minX !== undefined) {
      minX = bounds.minX;
      minY = bounds.minY;
      maxX = bounds.maxX;
      maxY = bounds.maxY;
    } else {
      minX = bounds.x;
      minY = bounds.y;
      maxX = bounds.x + bounds.width;
      maxY = bounds.y + bounds.height;
    }

    return [
      new google.maps.LatLng(minY, minX),
      new google.maps.LatLng(minY, maxX),
      new google.maps.LatLng(maxY, maxX),
      new google.maps.LatLng(maxY, minX),
    ];
  }

  // Same as boundsToPath but appends the first vertex at the end so a
  // Polyline (which has no auto-close) draws a fully-closed dashed border.
  _closedPathFromBounds(bounds) {
    const path = this.boundsToPath(bounds);
    if (path.length > 0) path.push(path[0]);
    return path;
  }

  boundsCenter(bounds) {
    // Array of corners → average all points
    if (Array.isArray(bounds) && bounds.length > 0) {
      const sum = bounds.reduce(
        (acc, c) => ({ x: acc.x + c.x, y: acc.y + c.y }),
        { x: 0, y: 0 }
      );
      return {
        lat: sum.y / bounds.length,
        lng: sum.x / bounds.length,
      };
    }

    if (bounds.minX !== undefined) {
      return {
        lat: (bounds.minY + bounds.maxY) / 2,
        lng: (bounds.minX + bounds.maxX) / 2,
      };
    }
    return {
      lat: bounds.y + bounds.height / 2,
      lng: bounds.x + bounds.width / 2,
    };
  }

  // Clear all product previews
  clearAllProductPreviews() {
    if (this.productPreviews) {
      Object.keys(this.productPreviews).forEach((id) => {
        this.productPreviews[id].polygon.setMap(null);
        this.productPreviews[id].label.setMap(null);
      });
      this.productPreviews = {};
    }

    // Remove active class from all preview buttons
    document
      .querySelectorAll('.js-nlc-maps-preview-btn.active')
      .forEach((btn) => {
        btn.classList.remove('active');
      });

    this.updateTogglePreviewsButton();
  }

  // Toggle all previews visibility
  toggleAllPreviews() {
    if (!this.productPreviews) return;

    const toggleBtn = document.querySelector(this.selectors.togglePreviewsBtn);
    const hasVisiblePreviews = Object.keys(this.productPreviews).length > 0;

    if (!hasVisiblePreviews && Object.keys(this.hiddenPreviews).length === 0)
      return;

    if (this.previewsVisible) {
      // Hide all previews — stash bounds + title + mode so we can fully
      // restore the same visual on re-show.
      this.hiddenPreviews = {};
      Object.keys(this.productPreviews).forEach((id) => {
        this.hiddenPreviews[id] = {
          bounds: this.productPreviews[id].bounds,
          title: this.productPreviews[id].title,
          mode: this.productPreviews[id].mode || 'selected',
        };
        this.productPreviews[id].polygon.setMap(null);
        this.productPreviews[id].label.setMap(null);
      });
      this.productPreviews = {};
      this.previewsVisible = false;
      if (toggleBtn) toggleBtn.classList.remove('active');
    } else {
      // Restore hidden previews — rebuild as dashed Polylines with the same
      // style helper as the live render path, so re-showing doesn't suddenly
      // change overlay design (used to draw solid #0066ff Polygon, which is
      // why toggling the "show/hide previews" button used to switch back to
      // the old solid look).
      Object.keys(this.hiddenPreviews).forEach((id) => {
        const data = this.hiddenPreviews[id];
        const restoredMode = data.mode === 'preview' ? 'preview' : 'selected';
        const style = this._previewStyleFor(restoredMode);
        const path = this._closedPathFromBounds(data.bounds);

        const previewPolygon = new google.maps.Polyline({
          path,
          clickable: true,
          strokeOpacity: 0,
          icons: this._dashedLineIcons(style.dashRepeat),
          strokeColor: style.strokeColor,
          strokeWeight: style.strokeWeight,
          map: this.map,
          zIndex: style.zIndexPolyline,
        });
        google.maps.event.addListener(previewPolygon, 'click', () => {
          this.toggleProductPreview(id, data.bounds, data.title, restoredMode);
        });

        const center = this.boundsCenter(data.bounds);

        const label = new google.maps.Marker({
          position: { lat: center.lat, lng: center.lng },
          map: this.map,
          clickable: true,
          title: (nlc_map_settings.click_to_hide_preview || 'Click to hide preview'),
          label: {
            text: (data.title || id) + '  ×',
            color: style.labelColor,
            fontWeight: 'bold',
            fontSize: '11px',
            className: 'nlc-maps-product-label',
          },
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 0,
          },
          cursor: 'pointer',
          zIndex: style.zIndexLabel,
        });
        google.maps.event.addListener(label, 'click', () => {
          this.toggleProductPreview(id, data.bounds, data.title, restoredMode);
        });

        this.productPreviews[id] = {
          polygon: previewPolygon,
          label: label,
          bounds: data.bounds,
          title: data.title,
          mode: restoredMode,
        };
      });
      this.hiddenPreviews = {};
      this.previewsVisible = true;
      if (toggleBtn) toggleBtn.classList.add('active');
    }

    // Save state AFTER action
    this.saveToHistory();
  }

  // Update toggle button visibility
  updateTogglePreviewsButton() {
    const toggleBtn = document.querySelector(this.selectors.togglePreviewsBtn);
    if (!toggleBtn) return;

    const hasAnyPreviews =
      (this.productPreviews && Object.keys(this.productPreviews).length > 0) ||
      (this.hiddenPreviews && Object.keys(this.hiddenPreviews).length > 0);

    toggleBtn.style.display = hasAnyPreviews ? 'flex' : 'none';
    this.updateClearPreviewsBtnVisibility();
  }
}

export default NlcMap;
